// Details.js - order files: Add File / paste (Ctrl+V) / drag & drop, each file with an optional note
// + Components / Receive-Receipt / Return (image files) that also change the order status
// + Order Status Images (OrderstatusImage page ki images is order ke liye yahan bhi dikhti hain)
// Layout: Image Files full width, Order Status Images full width, below it 2 columns (left: Scan/Zip/Other, right: Components/Receipt/Return)

import React, { useState, useEffect, useRef } from 'react';
import { IoAddCircleSharp } from "react-icons/io5";
import { FaRegEye, FaPaste, FaRegStickyNote } from "react-icons/fa";
import { MdOutlineDownloadForOffline } from "react-icons/md";
import { BsArrowRepeat } from "react-icons/bs";
import { RiDeleteBin6Fill } from "react-icons/ri";
import { FaSave } from "react-icons/fa";
import axios from 'axios';
import Swal from 'sweetalert2';

const API_BASE_URL = 'https://super-backend.hira-tech.co.uk';

const api = axios.create({ baseURL: API_BASE_URL });

const getAuthToken = () => {
  const token = localStorage.getItem('access_token') ||
                localStorage.getItem('token') ||
                localStorage.getItem('accessToken');
  if (token) return token;
  const possibleKeys = ['token', 'authToken', 'access_token', 'accessToken', 'jwt'];
  for (let key of possibleKeys) {
    const storedData = localStorage.getItem(key);
    if (storedData) {
      try {
        const parsed = JSON.parse(storedData);
        if (parsed.access || parsed.token || parsed.access_token) {
          return parsed.access || parsed.token || parsed.access_token;
        }
      } catch (e) { return storedData; }
    }
  }
  return null;
};

api.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

const MAX_NOTE = 2000;

// ================= NEW IMAGE CATEGORIES =================
// Sirf image files. File save hone par order ka status badal jata hai (neeche rules dekhein).
const IMAGE_ONLY_CATEGORIES = {
  component: {
    title: 'Components',
    status: 'Processing',
    icon: '🧩',
    empty: 'No component images uploaded',
  },
  receipt: {
    title: 'Receive / Receipt',
    status: 'Delivered',
    icon: '🧾',
    empty: 'No receive / receipt images uploaded',
  },
  return: {
    title: 'Return',
    status: 'Returned',
    icon: '↩️',
    empty: 'No return images uploaded',
  },
};

// Status ki tarteeb: status sirf aage barhta hai, kabhi peeche nahi jata
// (jaise Delivered order par component image add karne se wo wapas Processing nahi hoga).
const STATUS_RANK = {
  'Pending': 0,
  'Processing': 1,
  'In Process': 1,
  'Shipped': 2,
  'Delivered': 3,
  'Returned': 4,
};

// ================= LAYOUT + COMPACT CARD STYLES =================
const COMPACT_CSS = `
.details-split {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  align-items: start;
  margin-top: 16px;
}
.details-split-col {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
}
.details-compact .file-list-vertical {
  display: flex !important;
  flex-direction: row !important;
  flex-wrap: wrap !important;
  gap: 10px !important;
}
.details-compact .file-card-vertical {
  flex: 0 0 150px !important;
  width: 150px !important;
  max-width: 150px !important;
  padding: 8px !important;
  box-sizing: border-box !important;
  font-size: 11px !important;
}
.details-compact .file-thumbnail-vertical {
  height: 90px !important;
  min-height: 0 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  overflow: hidden !important;
  cursor: pointer;
}
.details-compact .file-thumbnail-img-vertical {
  max-width: 100% !important;
  max-height: 90px !important;
  width: auto !important;
  height: auto !important;
  object-fit: contain !important;
}
.details-compact .file-icon-large-vertical {
  font-size: 34px !important;
}
.details-compact .file-name-vertical {
  font-size: 11px !important;
  word-break: break-all;
  margin: 4px 0 !important;
}
.details-compact .file-action-icon-vertical {
  font-size: 14px !important;
}
.details-compact .file-icons-vertical {
  gap: 6px !important;
}
.details-compact .meta-line-vertical {
  font-size: 10px !important;
}
.details-compact .file-category h5 {
  font-size: 14px;
}
@media (max-width: 900px) {
  .details-split {
    grid-template-columns: 1fr;
  }
}
`;

const toast = (icon, title) =>
  Swal.fire({ icon, title, toast: true, position: 'top-end', timer: 2600, showConfirmButton: false });

const pad = (n) => String(n).padStart(2, '0');
const stamp = () => {
  const d = new Date();
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
};

// return popup ke liye HTML escape
const escapeHtml = (v) =>
  String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

const noteBoxStyle = {
  width: '100%',
  boxSizing: 'border-box',
  marginTop: 6,
  padding: '6px 8px',
  border: '1px solid #cfd8e6',
  borderRadius: 6,
  fontSize: 12,
  resize: 'vertical',
  fontFamily: 'inherit',
};

// note line shown in the meta area (in place of "From: Admin")
const noteLineStyle = {
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  textAlign: 'center',
  color: '#004aad',
  fontWeight: 600,
};

// props: products = order ke ordered products (return popup ke liye)
//        onProductsChanged = return marks save/clear hone par parent ko batane ke liye
const Details = ({ orderId, shopId, order, products = [], onOrderUpdated, onProductsChanged }) => {
  const [imageFiles, setImageFiles] = useState([]);
  const [scanFiles, setScanFiles] = useState([]);
  const [zipFiles, setZipFiles] = useState([]);
  const [otherFiles, setOtherFiles] = useState([]);
  const [componentFiles, setComponentFiles] = useState([]);
  const [receiptFiles, setReceiptFiles] = useState([]);
  const [returnFiles, setReturnFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // ✅ OrderstatusImage page ki images (is order ki)
  const [statusImages, setStatusImages] = useState([]);

  const [pendingFiles, setPendingFiles] = useState([]);
  // notes typed for files that are not saved yet: { [pendingId]: "text" }
  const [pendingNotes, setPendingNotes] = useState({});
  const [dragging, setDragging] = useState(false);

  // Ctrl+V kis section me paste ho: '' = pehle jaisa (auto), ya component / receipt / return
  const [activeCategory, setActiveCategoryState] = useState('');
  const activeCategoryRef = useRef('');
  const setActiveCategory = (category) => {
    activeCategoryRef.current = category;
    setActiveCategoryState(category);
  };

  const nameCounter = useRef(0);
  const addFilesRef = useRef(null);

  const getCurrentShopId = () => {
    if (shopId) return shopId;
    if (order && order.shop_id) return order.shop_id;
    if (order && order.shop && order.shop.shop_id) return order.shop.shop_id;
    if (order && order.shop_details && order.shop_details.shop_id) return order.shop_details.shop_id;

    const urlParams = new URLSearchParams(window.location.search);
    const urlShopId = urlParams.get('shop_id');
    if (urlShopId) return urlShopId;

    return localStorage.getItem('selectedShopId');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB');
    } catch {
      return dateString;
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-GB');
    } catch {
      return dateString;
    }
  };

  // har category ka apna list-setter
  const setterFor = (category) => {
    switch (category) {
      case 'image': return setImageFiles;
      case 'scan': return setScanFiles;
      case 'zip': return setZipFiles;
      case 'component': return setComponentFiles;
      case 'receipt': return setReceiptFiles;
      case 'return': return setReturnFiles;
      default: return setOtherFiles;
    }
  };

  const fetchFiles = async () => {
    if (!orderId) return;
    setLoading(true);
    try {
      const response = await api.get(`/order/order-files/${orderId}/`);
      const files = Array.isArray(response.data) ? response.data : [];

      const images = [];
      const scans = [];
      const zips = [];
      const others = [];
      const components = [];
      const receipts = [];
      const returns = [];

      files.forEach(file => {
        const fileData = {
          id: file.id,
          name: file.file_name,
          type: file.file_type,
          size: file.file_size,
          sizeDisplay: file.file_size_display,
          preview: ['image', 'component', 'receipt', 'return'].includes(file.file_type) ? file.file_url : null,
          url: file.file_url,
          date: formatDate(file.uploaded_at),
          updated: formatDate(file.uploaded_at),
          from: file.uploaded_by || 'Admin',
          note: file.note || '',
          isSaved: true
        };

        switch (file.file_type) {
          case 'image': images.push(fileData); break;
          case 'scan': scans.push(fileData); break;
          case 'zip': zips.push(fileData); break;
          case 'component': components.push(fileData); break;
          case 'receipt': receipts.push(fileData); break;
          case 'return': returns.push(fileData); break;
          default: others.push(fileData);
        }
      });

      setImageFiles(images);
      setScanFiles(scans);
      setZipFiles(zips);
      setOtherFiles(others);
      setComponentFiles(components);
      setReceiptFiles(receipts);
      setReturnFiles(returns);
    } catch (error) {
      console.error('Error fetching files:', error);
    } finally {
      setLoading(false);
    }
  };

  // ✅ OrderstatusImage page me jo images is order ke liye save hui hain, wo yahan layen
  const fetchStatusImages = async () => {
    if (!orderId) return;
    const currentShopId = getCurrentShopId();
    try {
      const res = await api.get('/order/order-status-images/', {
        params: currentShopId ? { shop_id: currentShopId } : {}
      });
      const result = res.data;

      let list = [];
      if (Array.isArray(result)) list = result;
      else if (result && Array.isArray(result.data)) list = result.data;
      else if (result && Array.isArray(result.results)) list = result.results;

      const mine = list
        .filter((item) => String(item.order_id ?? item.order) === String(orderId))
        .map((item) => ({
          id: item.id,
          image: item.image_url || item.image || '',
          status: item.status || '',
          city: item.city || '',
          dateRaw: item.date_uploaded || '',
          dateTime: formatDateTime(item.date_uploaded)
        }))
        .sort((a, b) => String(b.dateRaw).localeCompare(String(a.dateRaw)));

      setStatusImages(mine);
    } catch (error) {
      console.error('Error fetching order status images:', error);
    }
  };

  useEffect(() => {
    fetchFiles();
    fetchStatusImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  // Order Status Images page me kuch badle / is page par wapas aao to images dobara le aao
  useEffect(() => {
    const refresh = () => { fetchStatusImages(); };
    window.addEventListener('orderStatusImageUpdated', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      window.removeEventListener('orderStatusImageUpdated', refresh);
      window.removeEventListener('focus', refresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const getFileIcon = (type, name) => {
    if (type?.includes('pdf') || name?.endsWith('.pdf')) return '📄';
    if (type?.includes('word') || name?.includes('.doc')) return '📝';
    if (type?.includes('excel') || name?.includes('.xls')) return '📊';
    if (type?.includes('text') || name?.endsWith('.txt')) return '📃';
    return '📎';
  };

  const determineFileCategory = (file) => {
    const name = (file.name || '').toLowerCase();
    if ((file.type || '').startsWith('image/')) return 'image';
    if (name.endsWith('.stl') || name.endsWith('.obj') ||
        name.endsWith('.ply') || name.endsWith('.3mf')) return 'scan';
    if (name.endsWith('.zip') || name.endsWith('.rar') ||
        name.endsWith('.7z')) return 'zip';
    return 'other';
  };

  const addToLocalState = (file, category) => {
    setterFor(category)(prev => [...prev, file]);
  };

  const removeFromLocalState = (fileId, category) => {
    setterFor(category)(prev => prev.filter(f => f.id !== fileId));
  };

  const makePending = (file, category, note = '') => {
    const id = `pending_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const pending = {
      id,
      name: file.name,
      type: file.type,
      size: file.size,
      preview: (file.type || '').startsWith('image/') ? URL.createObjectURL(file) : null,
      date: formatDate(new Date()),
      updated: formatDateTime(new Date()),
      from: 'Admin',
      file,
      category,
      isSaved: false
    };
    setPendingFiles(prev => [...prev, pending]);
    setPendingNotes(prev => ({ ...prev, [id]: note }));
    addToLocalState(pending, category);
    return pending;
  };

  // one place for every way of adding files: Add File button, paste (Ctrl+V), drag & drop
  // forcedCategory: component / receipt / return (sirf image files); khali = pehle ki tarah auto
  const addFiles = (list, forcedCategory) => {
    const files = Array.from(list || []).filter(Boolean);
    if (files.length === 0) return 0;

    const imageOnly = forcedCategory && IMAGE_ONLY_CATEGORIES[forcedCategory];
    let added = 0;
    let rejected = 0;

    files.forEach((raw) => {
      let file = raw;
      // a screenshot / copied image arrives as "image.png" - give it a real, unique name
      if (!raw.name || /^image\.\w+$/i.test(raw.name)) {
        nameCounter.current += 1;
        const ext = ((raw.type || 'image/png').split('/')[1] || 'png')
          .replace('jpeg', 'jpg').replace('svg+xml', 'svg');
        file = new File([raw], `pasted-${stamp()}-${nameCounter.current}.${ext}`, { type: raw.type || 'image/png' });
      }

      if (imageOnly && !(file.type || '').startsWith('image/')) {
        rejected += 1;
        return;
      }

      makePending(file, forcedCategory || determineFileCategory(file));
      added += 1;
    });

    if (rejected > 0) {
      toast('warning', `${rejected} file(s) skipped - only image files are allowed in ${IMAGE_ONLY_CATEGORIES[forcedCategory].title}`);
    }
    return added;
  };
  addFilesRef.current = addFiles;

  const handleFileUpload = (event) => {
    setActiveCategory('');
    addFiles(event.target.files);
    event.target.value = '';
  };

  // Components / Receive-Receipt / Return section ka apna Add button
  const handleSectionUpload = (category) => (event) => {
    setActiveCategory(category);
    addFiles(event.target.files, category);
    event.target.value = '';
  };

  // ---- paste (Ctrl+V) anywhere on this tab ----
  useEffect(() => {
    const onPaste = (e) => {
      const target = e.target;
      const tag = (target && target.tagName ? target.tagName : '').toLowerCase();
      // while typing in a note / any field, pasting must stay normal text pasting
      if (tag === 'input' || tag === 'textarea' || (target && target.isContentEditable)) return;

      const items = e.clipboardData && e.clipboardData.items ? Array.from(e.clipboardData.items) : [];
      const files = items
        .filter((it) => it.kind === 'file')
        .map((it) => it.getAsFile())
        .filter(Boolean);
      if (files.length === 0) return;

      e.preventDefault();
      const category = activeCategoryRef.current || undefined;
      const count = addFilesRef.current(files, category);
      if (count) {
        const where = category ? ` into ${IMAGE_ONLY_CATEGORIES[category].title}` : '';
        toast('success', `${count} file(s) pasted${where} - add a note if you like, then press Save Files`);
      }
    };

    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, []);

  // button for people who prefer a click over Ctrl+V (works on https / localhost in Chrome, Edge, Safari)
  const pasteFromClipboard = async (category) => {
    if (category) setActiveCategory(category);
    try {
      if (!navigator.clipboard || !navigator.clipboard.read) throw new Error('unsupported');
      const clipItems = await navigator.clipboard.read();
      const files = [];
      for (const item of clipItems) {
        for (const type of item.types) {
          if (type.startsWith('image/')) {
            const blob = await item.getType(type);
            files.push(new File([blob], `image.${type.split('/')[1] || 'png'}`, { type }));
          }
        }
      }
      if (files.length === 0) {
        toast('info', 'No image found in the clipboard. Copy an image first.');
        return;
      }
      addFiles(files, category);
      toast('success', `${files.length} image(s) pasted`);
    } catch (err) {
      toast('info', 'Click on this page and press Ctrl+V (Cmd+V on Mac) to paste.');
    }
  };

  // ---- drag & drop (top zone: auto, sections: that category) ----
  const onDragOver = (e) => {
    e.preventDefault();
    if (!dragging) setDragging(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    setDragging(false);
  };
  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    setActiveCategory('');
    addFiles(e.dataTransfer && e.dataTransfer.files);
  };

  const onSectionDrop = (category) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveCategory(category);
    addFiles(e.dataTransfer && e.dataTransfer.files, category);
  };

  // ✅ Files save hone ke baad order ka status badlo:
  //   Components -> Processing, Receive / Receipt -> Delivered, Return -> Returned
  // Status sirf aage jata hai (peeche nahi), aur Canceled order ko chhua nahi jata.
  const applyStatusFromFiles = async (categories) => {
    const targets = categories
      .map((c) => IMAGE_ONLY_CATEGORIES[c] && IMAGE_ONLY_CATEGORIES[c].status)
      .filter(Boolean);
    if (targets.length === 0) return { changed: null, failed: false };

    // ek hi batch me kai types hon to sab se aage wala status
    const target = targets.sort((a, b) => (STATUS_RANK[b] || 0) - (STATUS_RANK[a] || 0))[0];
    const current = (order && (order.order_Status || order.status)) || 'Pending';

    if (current === 'Canceled' || current === target) return { changed: null, failed: false };
    if ((STATUS_RANK[target] || 0) <= (STATUS_RANK[current] ?? 0)) return { changed: null, failed: false };

    try {
      const payload = { order_Status: target };
      // total ko jyun ka tyun rakho (status badalne se amount dobara calculate na ho)
      if (order && order.total_Amount !== undefined && order.total_Amount !== null) {
        payload.total_Amount = order.total_Amount;
      }
      const res = await api.put(`/order/update-order-details/${orderId}/`, payload);
      if (onOrderUpdated) onOrderUpdated(res.data);
      window.dispatchEvent(new Event('orderUpdated'));
      return { changed: target, failed: false };
    } catch (error) {
      console.error('Error updating order status:', error);
      return { changed: null, failed: true };
    }
  };

  // ================= RETURN POPUP =================
  // returns: array of {ordered_product_id, returned_qty}, [] (koi product nahi), ya null (cancel)
  const askReturnedProducts = async () => {
    const list = products || [];
    if (list.length === 0) return [];

    const rows = list.map(p => `
      <label style="display:flex;align-items:center;gap:10px;padding:8px;border-bottom:1px solid #eee;text-align:left">
        <input type="checkbox" class="rt-check" data-id="${p.id}" ${p.returnedQty > 0 ? 'checked' : ''}>
        <span style="flex:1;font-size:14px">${escapeHtml(p.name)} <small style="color:#64748b">(ordered: ${p.qty})</small></span>
        <input type="number" class="rt-qty" data-id="${p.id}" min="1" max="${p.qty}" value="${p.returnedQty || p.qty}" style="width:64px;padding:4px">
      </label>`).join('');

    const res = await Swal.fire({
      title: 'Which products were returned?',
      html: `<div style="max-height:320px;overflow-y:auto">${rows}</div>`,
      showCancelButton: true,
      confirmButtonText: 'Continue & Save',
      confirmButtonColor: '#004aad',
      preConfirm: () => {
        const out = [];
        let bad = null;
        Array.from(document.querySelectorAll('.rt-check')).forEach((cb) => {
          if (!cb.checked || bad) return;
          const p = list.find(x => String(x.id) === cb.dataset.id);
          const q = parseInt(document.querySelector(`.rt-qty[data-id="${cb.dataset.id}"]`).value, 10) || 0;
          if (q < 1 || q > p.qty) bad = `Invalid quantity for ${p.name}`;
          else out.push({ ordered_product_id: p.id, returned_qty: q });
        });
        if (bad) { Swal.showValidationMessage(bad); return false; }
        if (out.length === 0) { Swal.showValidationMessage('Select at least one product'); return false; }
        return out;
      }
    });
    return res.isConfirmed ? res.value : null;
  };

  const handleSaveFiles = async () => {
    if (pendingFiles.length === 0) {
      Swal.fire({
        icon: 'info',
        title: 'No Files to Save',
        text: 'Please add files first.',
        confirmButtonColor: '#004aad'
      });
      return;
    }

    const currentShopId = getCurrentShopId();
    if (!currentShopId) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Shop ID not found. Please refresh the page.',
        confirmButtonColor: '#004aad'
      });
      return;
    }

    // Return image ho to pehle products poochho
    let returned = [];
    if (pendingFiles.some(f => f.category === 'return')) {
      returned = await askReturnedProducts();
      if (returned === null) return; // user ne cancel kiya
    }

    setSaving(true);

    try {
      const uploadPromises = pendingFiles.map(async (pendingFile) => {
        const formData = new FormData();
        formData.append('file', pendingFile.file);
        formData.append('order_id', orderId);
        formData.append('file_type', pendingFile.category);
        formData.append('shop_id', currentShopId);
        formData.append('uploaded_by', 'Admin');
        formData.append('note', (pendingNotes[pendingFile.id] || '').trim());

        return api.post('/order/order-files/create/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      });

      const results = await Promise.all(uploadPromises);

      // the server sends the saved file back; if a note was typed but the answer has no "note" field,
      // the backend has not been updated yet and the note was NOT stored
      const noteNotStored = results.some((res, i) => {
        const typed = (pendingNotes[pendingFiles[i].id] || '').trim();
        return typed && !(res && res.data && Object.prototype.hasOwnProperty.call(res.data, 'note'));
      });

      // files save ho gayi: ab status (Components / Receipt / Return ke hisaab se)
      const savedCount = pendingFiles.length;
      const statusResult = await applyStatusFromFiles(pendingFiles.map((f) => f.category));

      // return marks save karo (agar user ne koi product tick kiya)
      if (returned.length > 0) {
        try {
          await api.post('/order/return-items/', { order_id: orderId, items: returned });
          if (onProductsChanged) onProductsChanged();
        } catch (err) {
          console.error('Error saving returned products:', err);
          toast('warning', 'Files saved, but returned products could not be saved.');
        }
      }

      if (noteNotStored) {
        Swal.fire({
          icon: 'warning',
          title: 'Files saved - but notes were NOT saved',
          text: 'The server did not return the note field. Update the backend (OrderFile.note + migration, ' +
                "'note' in OrderFileSerializer, and the new order_file_views in urls.py), then add the notes again.",
          confirmButtonColor: '#004aad'
        });
      } else if (statusResult.failed) {
        Swal.fire({
          icon: 'warning',
          title: 'Files saved',
          text: `${savedCount} file(s) saved, but the order status could not be updated automatically. Please change it manually.`,
          confirmButtonColor: '#004aad'
        });
      } else {
        Swal.fire({
          icon: 'success',
          title: 'Files Saved!',
          text: statusResult.changed
            ? `${savedCount} file(s) saved. Order status is now "${statusResult.changed}".`
            : `${savedCount} file(s) saved successfully.`,
          timer: statusResult.changed ? 3000 : 2000,
          showConfirmButton: false
        });
      }

      setPendingFiles([]);
      setPendingNotes({});
      await fetchFiles();

    } catch (error) {
      console.error('Error saving files:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error?.response?.data?.error || 'Failed to save some files. Please try again.',
        confirmButtonColor: '#004aad'
      });
    } finally {
      setSaving(false);
    }
  };

  const viewFile = (file) => {
    if (file.url) {
      window.open(file.url, '_blank');
    } else if (file.preview) {
      window.open(file.preview, '_blank');
    } else if (file.file) {
      const url = URL.createObjectURL(file.file);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
  };

  const downloadFile = (file) => {
    const url = file.url || file.preview;
    if (url) {
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else if (file.file) {
      const fileUrl = URL.createObjectURL(file.file);
      const a = document.createElement('a');
      a.href = fileUrl;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(fileUrl), 1000);
    }
  };

  const replaceFile = (file, category) => {
    const input = document.createElement('input');
    input.type = 'file';
    if (IMAGE_ONLY_CATEGORIES[category]) input.accept = 'image/*';
    input.onchange = async (e) => {
      const newFileData = e.target.files[0];
      if (!newFileData) return;

      if (IMAGE_ONLY_CATEGORIES[category] && !(newFileData.type || '').startsWith('image/')) {
        toast('warning', `Only image files are allowed in ${IMAGE_ONLY_CATEGORIES[category].title}`);
        return;
      }

      const isPending = String(file.id).startsWith('pending_');

      if (isPending) {
        const oldNote = pendingNotes[file.id] || '';
        removeFromLocalState(file.id, category);
        setPendingFiles(prev => prev.filter(f => f.id !== file.id));
        setPendingNotes(prev => {
          const next = { ...prev };
          delete next[file.id];
          return next;
        });
        makePending(newFileData, category, oldNote);
      } else {
        try {
          await api.delete(`/order/order-files/${file.id}/delete/`);
          removeFromLocalState(file.id, category);
          // the note of the old file moves to the new one
          makePending(newFileData, category, file.note || '');
        } catch (error) {
          console.error('Error replacing file:', error);
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'Failed to replace file.',
            confirmButtonColor: '#004aad'
          });
        }
      }
    };
    input.click();
  };

  const removeFile = async (fileId, category) => {
    const result = await Swal.fire({
      title: 'Delete File',
      text: 'Are you sure you want to delete this file?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#004aad',
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) return;

    const isPending = String(fileId).startsWith('pending_');

    if (isPending) {
      setPendingFiles(prev => prev.filter(f => f.id !== fileId));
      setPendingNotes(prev => {
        const next = { ...prev };
        delete next[fileId];
        return next;
      });
      removeFromLocalState(fileId, category);
    } else {
      try {
        const res = await api.delete(`/order/order-files/${fileId}/delete/`);
        const reverted = res.data && res.data.reverted_status;

        if (reverted) {
          if (onOrderUpdated) onOrderUpdated({ ...order, order_Status: reverted });
          window.dispatchEvent(new Event('orderUpdated'));
        }
        if (res.data && res.data.returns_cleared && onProductsChanged) onProductsChanged();

        removeFromLocalState(fileId, category);
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: reverted
            ? `File deleted. Order status is back to "${reverted}".`
            : 'File has been deleted.',
          timer: 2000,
          showConfirmButton: false
        });
      } catch (error) {
        console.error('Error deleting file:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to delete file.',
          confirmButtonColor: '#004aad'
        });
      }
    }
  };

  // change the note of a file that is already saved
  const editSavedNote = async (file, category) => {
    const result = await Swal.fire({
      title: file.note ? 'Edit note' : 'Add a note',
      input: 'textarea',
      inputValue: file.note || '',
      inputPlaceholder: 'Write a note for this file...',
      inputAttributes: { maxlength: MAX_NOTE },
      showCancelButton: true,
      confirmButtonColor: '#004aad',
      confirmButtonText: 'Save note'
    });
    if (!result.isConfirmed) return;

    const note = (result.value || '').trim();
    try {
      await api.patch(`/order/order-files/${file.id}/update/`, { note });
      setterFor(category)(list => list.map(f => (f.id === file.id ? { ...f, note } : f)));
      toast('success', 'Note saved');
    } catch (error) {
      console.error('Error saving note:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error?.response?.data?.error || 'Could not save the note.',
        confirmButtonColor: '#004aad'
      });
    }
  };

  const categoryIcon = (category, file) => {
    if (IMAGE_ONLY_CATEGORIES[category]) return IMAGE_ONLY_CATEGORIES[category].icon;
    return category === 'image' ? '🖼️' :
           category === 'scan' ? '🔍' :
           category === 'zip' ? '📦' :
           getFileIcon(file.type, file.name);
  };

  const renderFileCard = (file, category) => {
    // saved file -> note from the server, pending file -> what is being typed right now
    const noteText = file.isSaved ? file.note : (pendingNotes[file.id] || '');

    return (
      <div key={file.id} className="file-card-vertical">
        <div className="file-thumbnail-vertical" onClick={() => viewFile(file)}>
          {file.preview ? (
            <img src={file.preview} alt={file.name} className="file-thumbnail-img-vertical" />
          ) : (
            <span className="file-icon-large-vertical">
              {categoryIcon(category, file)}
            </span>
          )}
        </div>
        <div className="file-name-vertical">
          {file.name}
          {!file.isSaved && <span className="pending-badge"> (Pending)</span>}
        </div>
        <div className="file-icons-vertical">
          <FaRegEye className="file-action-icon-vertical" onClick={() => viewFile(file)} title="View" />
          <MdOutlineDownloadForOffline className="file-action-icon-vertical" onClick={() => downloadFile(file)} title="Download" />
          <BsArrowRepeat className="file-action-icon-vertical" onClick={() => replaceFile(file, category)} title="Replace" />
          {file.isSaved && (
            <FaRegStickyNote
              className="file-action-icon-vertical"
              onClick={() => editSavedNote(file, category)}
              title={file.note ? 'Edit note' : 'Add note'}
            />
          )}
          <RiDeleteBin6Fill className="file-action-icon-vertical delete-icon-vertical" onClick={() => removeFile(file.id, category)} title="Delete" />
        </div>
        <div className="file-meta-vertical">
          <div className="meta-line-vertical">Date: {file.date}</div>
          <div className="meta-line-vertical">Updated: {file.updated}</div>
          {/* "From: Admin" ki jaga ab note show hota ha */}
          <div className="meta-line-vertical" style={noteLineStyle}>
            Note: {noteText ? noteText : '-'}
          </div>
        </div>

        {!file.isSaved && (
          <textarea
            rows={2}
            maxLength={MAX_NOTE}
            style={noteBoxStyle}
            placeholder="Add a note for this file (optional)"
            value={pendingNotes[file.id] || ''}
            onChange={(e) => {
              const value = e.target.value;
              setPendingNotes(prev => ({ ...prev, [file.id]: value }));
            }}
          />
        )}
      </div>
    );
  };

  // ✅ Order Status Images page ki image ka (sirf dekhne wala) card
  const renderStatusImageCard = (img) => {
    const fileLike = { url: img.image, name: `${img.status || 'status'}-${img.id}.jpg` };
    return (
      <div key={`si-${img.id}`} className="file-card-vertical">
        <div className="file-thumbnail-vertical" onClick={() => viewFile(fileLike)}>
          {img.image ? (
            <img src={img.image} alt={img.status} className="file-thumbnail-img-vertical" />
          ) : (
            <span className="file-icon-large-vertical">🖼️</span>
          )}
        </div>
        <div className="file-name-vertical">{img.status || '-'}</div>
        <div className="file-icons-vertical">
          <FaRegEye className="file-action-icon-vertical" onClick={() => viewFile(fileLike)} title="View" />
          <MdOutlineDownloadForOffline className="file-action-icon-vertical" onClick={() => downloadFile(fileLike)} title="Download" />
        </div>
        <div className="file-meta-vertical">
          <div className="meta-line-vertical">City: {img.city || '-'}</div>
          <div className="meta-line-vertical">Date: {img.dateTime}</div>
        </div>
      </div>
    );
  };

  // Components / Receive-Receipt / Return: apna Add + Paste button, drag & drop, aur status ka hint
  const renderImageOnlySection = (category, files) => {
    const meta = IMAGE_ONLY_CATEGORIES[category];
    const isActive = activeCategory === category;

    return (
      <div
        className="file-category"
        onClick={() => setActiveCategory(category)}
        onDragOver={(e) => e.preventDefault()}
        onDrop={onSectionDrop(category)}
        style={{
          borderRadius: 8,
          outline: isActive ? '2px solid #004aad' : '2px solid transparent',
          outlineOffset: 4,
          transition: 'outline-color 0.15s ease',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <h5 style={{ margin: 0 }}>
            {meta.title}
            <small style={{ marginLeft: 10, fontWeight: 400, color: '#64748b', fontSize: 11 }}>
              (images only - saving sets status to {meta.status})
            </small>
          </h5>
          <div style={{ display: 'flex', gap: 8 }}>
            <label className="add-file-btn" style={{ padding: '4px 12px', fontSize: 12 }}>
              <IoAddCircleSharp size={16} />
              Add
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleSectionUpload(category)}
                style={{ display: 'none' }}
              />
            </label>
            <button
              type="button"
              className="add-file-btn"
              onClick={(e) => { e.stopPropagation(); pasteFromClipboard(category); }}
              title={`Paste an image into ${meta.title}`}
              style={{ border: 'none', cursor: 'pointer', padding: '4px 12px', fontSize: 12 }}
            >
              <FaPaste size={13} />
              Paste
            </button>
          </div>
        </div>
        <div className="file-list-vertical" style={{ marginTop: 10 }}>
          {files.map(file => renderFileCard(file, category))}
          {files.length === 0 && (
            <div className="no-files">{meta.empty}</div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="details-content">
      <style>{COMPACT_CSS}</style>

      <div className="details-header">
        <h4>File Details</h4>
        <div className="details-header-actions">
          <label className="add-file-btn">
            <IoAddCircleSharp size={20} />
            Add File
            <input
              type="file"
              multiple
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
          </label>

          <button
            type="button"
            className="add-file-btn"
            onClick={() => { setActiveCategory(''); pasteFromClipboard(); }}
            title="Paste an image from the clipboard (or just press Ctrl+V)"
            style={{ border: 'none', cursor: 'pointer' }}
          >
            <FaPaste size={16} />
            Paste
          </button>

          {pendingFiles.length > 0 && (
            <button
              className="save-files-btn"
              onClick={handleSaveFiles}
              disabled={saving}
            >
              <FaSave size={16} />
              {saving ? 'Saving...' : `Save Files (${pendingFiles.length})`}
            </button>
          )}
        </div>
      </div>

      {/* drop zone */}
      <div
        onClick={() => setActiveCategory('')}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        style={{
          margin: '0 0 16px',
          padding: '12px 16px',
          border: `2px dashed ${dragging ? '#004aad' : '#c3d3ee'}`,
          background: dragging ? '#eaf2ff' : '#f8fbff',
          borderRadius: 8,
          textAlign: 'center',
          fontSize: 13,
          color: '#475569',
          transition: 'all 0.15s ease',
        }}
      >
        Press <strong>Ctrl+V</strong> to paste an image, <strong>drag &amp; drop</strong> files here, or use <strong>Add File</strong>.
        Each file can have its own note.
        {activeCategory && (
          <div style={{ marginTop: 6, color: '#004aad', fontWeight: 600 }}>
            Ctrl+V will paste into: {IMAGE_ONLY_CATEGORIES[activeCategory].title}
            {' '}
            <span
              style={{ textDecoration: 'underline', cursor: 'pointer', fontWeight: 400 }}
              onClick={(e) => { e.stopPropagation(); setActiveCategory(''); }}
            >
              (reset)
            </span>
          </div>
        )}
      </div>

      {loading ? (
        <div className="no-files">Loading files...</div>
      ) : (
        <div className="file-categories details-compact">
          {/* Image Files - full width */}
          <div className="file-category" onClick={() => setActiveCategory('')}>
            <h5>Image Files</h5>
            <div className="file-list-vertical">
              {imageFiles.map(file => renderFileCard(file, 'image'))}
              {imageFiles.length === 0 && (
                <div className="no-files">No image files uploaded</div>
              )}
            </div>
          </div>

          {/* ✅ Order Status Images - full width (Order Status Images page se linked) */}
          <div className="file-category" onClick={() => setActiveCategory('')}>
            <h5>
              Order Status Images
              <small style={{ marginLeft: 10, fontWeight: 400, color: '#64748b', fontSize: 11 }}>
                (added from the Order Status Images page)
              </small>
            </h5>
            <div className="file-list-vertical">
              {statusImages.map(img => renderStatusImageCard(img))}
              {statusImages.length === 0 && (
                <div className="no-files">No order status images for this order</div>
              )}
            </div>
          </div>

          {/* 2 columns: LEFT = Scan / Zip / Other, RIGHT = Components / Receipt / Return */}
          <div className="details-split">
            {/* LEFT HALF */}
            <div className="details-split-col">
              {/* Intraoral Scan Files */}
              <div className="file-category" onClick={() => setActiveCategory('')}>
                <h5>Intraoral Scan Files</h5>
                <div className="file-list-vertical">
                  {scanFiles.map(file => renderFileCard(file, 'scan'))}
                  {scanFiles.length === 0 && (
                    <div className="no-files">No intraoral scan files uploaded</div>
                  )}
                </div>
              </div>

              {/* Zip Files */}
              <div className="file-category" onClick={() => setActiveCategory('')}>
                <h5>Zip Files</h5>
                <div className="file-list-vertical">
                  {zipFiles.map(file => renderFileCard(file, 'zip'))}
                  {zipFiles.length === 0 && (
                    <div className="no-files">No zip files uploaded</div>
                  )}
                </div>
              </div>

              {/* Other Files */}
              <div className="file-category" onClick={() => setActiveCategory('')}>
                <h5>Other Files</h5>
                <div className="file-list-vertical">
                  {otherFiles.map(file => renderFileCard(file, 'other'))}
                  {otherFiles.length === 0 && (
                    <div className="no-files">No other files uploaded</div>
                  )}
                </div>
              </div>
            </div>

            {/* RIGHT HALF: image files, status auto-change */}
            <div className="details-split-col">
              {renderImageOnlySection('component', componentFiles)}
              {renderImageOnlySection('receipt', receiptFiles)}
              {renderImageOnlySection('return', returnFiles)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Details;